<script setup>
  import { Trash } from "lucide-vue-next";
  import { storeToRefs } from "pinia";
  import { Button } from "@/components/ui/button";
  import { Switch } from "@/components/ui/switch";
  import { ConfirmDialog } from "@/components/episto-ui/confirm-dialog";
  import ListContainer from "@/components/episto-ui/list/ListContainer.vue";
  import ListItem from "@/components/episto-ui/list/ListItem.vue";
  import { MediaThumb } from "@/components/episto-ui/media-thumb";
  import { useMCreativesStore } from "@stores/mCreatives";
  import { useCreativeSummary } from "@composables/useCreativeSummary";

  const mCreativesStore = useMCreativesStore();
  const { creatives } = storeToRefs(mCreativesStore);
  const {
    selectCreative,
    duplicateCreative,
    removeCreative,
    addCreative,
    isCreativeSelected,
    isNewCreative,
    isRemovableCreative,
    updateCreativeStatus,
    creativeErrorMessages,
  } = mCreativesStore;
  const { thumbnail, headline } = useCreativeSummary();

  const creativeErrorsMessages = (index) => creativeErrorMessages(creatives.value[index]);
</script>

<template>
  <ListContainer
    :add-element="addCreative"
    element-name="creative"
    :show-header="false">
    <template #elements>
      <ListItem
        v-for="(creative, index) in creatives"
        :key="`creative-item-${index}`"
        :index="index"
        :element-title="creative.name"
        :element-subtitle="headline(creative)"
        :select-element="selectCreative"
        :duplicate-element="duplicateCreative"
        :is-element-selected="isCreativeSelected"
        :element-error-messages="creativeErrorsMessages">
        <template #media>
          <MediaThumb
            :src="thumbnail(creative)?.url"
            :media-type="thumbnail(creative)?.mediaType"
            :alt="creative.name" />
        </template>
        <template #actions>
          <ConfirmDialog
            v-if="isRemovableCreative(creative)"
            title="Delete creative"
            description="Are you sure you want to delete this creative?"
            confirm-label="Delete"
            @confirm="removeCreative(index)">
            <template #trigger>
              <Button
                variant="ghost"
                size="icon-sm">
                <Trash class="h-4 w-4" />
              </Button>
            </template>
          </ConfirmDialog>
          <Switch
            v-if="!isNewCreative(creative)"
            size="sm"
            :model-value="creative.status === 'active'"
            @update:model-value="updateCreativeStatus(index, $event ? 'active' : 'inactive')" />
        </template>
      </ListItem>
    </template>
  </ListContainer>
</template>
